import {
    AuthContext,
    BaseButton,
    CountBadge,
    CoverageType,
    DataTable,
    DataTablePresenter,
    FilterMethod,
    filtersKeptOnTabSwitch,
    getOptions,
    getPayerLanes,
    HALTED_PAYERS_NOTICE,
    IAuthContextProps,
    ICustomFilter,
    IFilterSchema,
    IPayerCreatePayload,
    IPayerCreateValues,
    ITableColumn,
    ITableFetchOptions,
    IToastContextProps,
    ListProviderPayersRequest,
    Notice,
    PathCell,
    PayerTab,
    payerTabFilters,
    payerTabHolds,
    PayerCreateForm,
    PayerDrawer,
    PayerDrawerPanel,
    PayerRoutingPanel,
    buildPayerColumns,
    PayerLaneId,
    ProviderPayer,
    ProviderPayerSortType,
    TabContainer,
    TableExport,
    ToastContext,
    UNKNOWN_PATH,
} from '@enter/core';
import { mdiChevronRight, mdiMagnify, mdiOpenInNew, mdiPlus, mdiUnfoldLessHorizontal, mdiUnfoldMoreHorizontal } from '@mdi/js';
import { Icon } from '@mdi/react';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import _ from 'lodash';
import * as mobx from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { CellProps, Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy, PayerRoutingStore } from 'stores';
import { GENERATED_PAYER_NAME_PREFIX, generatedPayerName } from 'stores/generated-payer.util';
import * as S from './payers.styles';

/**
 * Payers, from the provider's side: where their claims, appeals and paperwork actually go.
 *
 * **Read-only, apart from adding a payer.** @John Russell, 2026-08-25: *"We don't want to
 * surface editing payers to the provider page right now… They submit the payer, ENTER reviews
 * and configures them."* Jordan Kelley, 2026-09-11, asked for that submit step (FE-79): **New
 * payer** creates one, the backend names it "Generated Payer - …", and it lands in ENTER
 * Support's Unconfigured payers queue. There is still no edit, mapping or halt control — the
 * provider API has no update — so everything after the create is ENTER's.
 *
 * Not to be confused with the `payers-providers` screen, which is payer↔provider ENROLMENT.
 * This is routing.
 *
 * The lane derivation is `packages/core/src/services/payer-routing/`, shared with the support
 * console and covered by its 63 tests, so the two apps cannot disagree about where a payer's
 * data goes — the one thing they most need to agree on.
 */




/**
 * The search box owns its text.
 *
 * `renderToolbar` is called from inside `DataTable`'s render, which is an observer, so reading
 * `PayersPage.state` there reads non-observable state inside someone else's derivation — which
 * mobx-react refuses outright. The typed value is transient UI state belonging to the input;
 * only the debounced result needs to reach the presenter.
 *
 * Declared at module scope so React keeps its state across the parent's re-renders. An inline
 * component would be a new type every render and would remount on every keystroke.
 *
 * "payer ID", not "EDI number" — the same thing, in the word the people asking actually use.
 * It is not the EMR payer ID, which is a tag and has its own filter in the panel.
 */
const PayersSearch: React.FunctionComponent<{ onSearch: (value: string) => void }> = ({ onSearch }) => {
    const [value, setValue] = React.useState('');

    return (
        <S.SearchSlot>
            <S.Search>
                <Icon path={mdiMagnify} size={0.8} />
            <S.SearchInput
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    setValue(e.target.value);
                    onSearch(e.target.value);
                }}
                placeholder="Search payers by name or payer ID..."
                aria-label="Search payers by name or payer ID"
            />
            </S.Search>
        </S.SearchSlot>
    );
};

/**
 * The one control on this screen, and it opens a read-only drawer.
 *
 * `stopPropagation` because the row itself is a click target that toggles expansion — without
 * it, opening the drawer would also expand the row behind it.
 */
function detailColumn(onOpen: (payer: ProviderPayer) => void): ITableColumn<ProviderPayer> {
    const Cell = ({ row }: CellProps<ProviderPayer>): React.ReactElement => (
        <S.DetailButton
            type="button"
            aria-label={`Open detail for ${row.original.name}`}
            title="Open detail"
            onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                onOpen(row.original);
            }}
        >
            <Icon path={mdiOpenInNew} size={0.7} />
        </S.DetailButton>
    );

    return { id: 'Detail', Header: '', accessor: (): string => '', Cell, disableSortBy: true, maxWidth: 48 };
}

/**
 * What the New payer dialog says before the user commits: the name that will actually be saved,
 * and who finishes the job. Without it a provider types "Aetna West", finds "Generated Payer -
 * Aetna West" in the list, and reasonably thinks something went wrong.
 */
const newPayerNotice = (values: IPayerCreateValues): React.ReactNode => (
    <>
        Saved as <strong>{values.name.trim() ? generatedPayerName(values.name) : `${GENERATED_PAYER_NAME_PREFIX}…`}</strong>.
        ENTER Support reviews new payers and finishes setting them up.
    </>
);


/**
 * Name, and the EMR payer identifier.
 *
 * ENTER's naming convention appends the EDI number to the payer name for any payer with a
 * clearinghouse connection ("CoreSource (Commercial) [35182]"), so a `LK` on the name matches
 * that too — two of the three things someone would search for.
 *
 * The panel offers twelve filters, not two. `filterStarred` and `filterActive` are the ONLY
 * two fields `ListSupportPayersRequest` has that this request does not — verified by diffing
 * the two interfaces, 18 fields shared — and both are support concepts: starring is an
 * operator's bookmark and active/disabled is an admin state a provider must not act on. So
 * every filter the support screen offers that CAN exist here now does.
 *
 * Each needs a distinct `filterId`: `Filters` resolves a schema with
 * `filterSchemas.find(s => s.filterId === filter.filterId)`, so two entries pointing at the
 * same field silently collapse into one.
 *
 * The EMR payer identifier is a SEPARATE filter rather than part of the name search on purpose. `filterName` and `filterTag` are different filters: the server ORs
 * repeated values of ONE filter, so a single box writing both would ask for name AND tag and
 * quietly return the intersection — usually nothing. Two filters, each honest about what it
 * matches, beats one box that is wrong in a way nobody can see.
 *
 * `filterTag` VERIFIED WORKING against production 2026-08-25, after John shipped it: it
 * narrowed 50 payers to 1, and a nonsense tag returned zero rather than the full list — the
 * control that distinguishes a filter being applied from a filter being ignored.
 */
const filterSchemas: IFilterSchema<keyof ListProviderPayersRequest>[] = [
    { label: 'Name', filterId: 'filterName', columnId: 'Name', filterType: 'String' },
    { label: 'EMR payer ID', filterId: 'filterTag', columnId: 'EmrPayers', filterType: 'String' },
    { label: 'ENTER ID (internal UUID)', filterId: 'filterId', columnId: 'ID', filterType: 'String' },
    { label: 'Created Date', filterId: 'filterCreated', columnId: 'Created', filterType: 'DateTime' },
    {
        label: 'Coverage type',
        filterId: 'filterCoverageType',
        columnId: 'CoverageType',
        filterType: 'Select',
        options: getOptions(CoverageType, { normalize: true }),
    },
    { label: 'City', filterId: 'filterAddressCity', columnId: 'AddressCity', filterType: 'String' },
    { label: 'State', filterId: 'filterAddressState', columnId: 'AddressState', filterType: 'String' },
    { label: 'Postal Code', filterId: 'filterAddressPostalCode', columnId: 'PostalCode', filterType: 'String' },
    { label: 'Country Code', filterId: 'filterAddressCountryCode', columnId: 'CountryCode', filterType: 'String' },
    { label: 'Email', filterId: 'filterEmail', columnId: 'Email', filterType: 'String' },
    { label: 'Phone', filterId: 'filterPhone', columnId: 'Phone', filterType: 'String' },
    { label: 'Fax', filterId: 'filterFax', columnId: 'Fax', filterType: 'String' },
];

/**
 * The expanded row and the drawer, each as its own reactive component.
 *
 * Both read `getBindings`, which resolves after the list has painted. Computing that in
 * `PayersPage.render` and passing it down does not work: the page is not an observer, so it
 * never re-renders when the join lands and the surface stays on "Loading clearinghouse
 * connections…" — the drawer visibly so.
 *
 * Making the PAGE an observer is not the fix either: mobx-react refuses to read `this.props`
 * inside a class observer's derivation, and this page's render reads `props.rootStore` and
 * `props.authContext`. A function component has no such restriction, so the reactivity goes
 * exactly where the observable is read.
 */
const ReactiveRoutingPanel = observer(
    ({ payer, store }: { payer: ProviderPayer; store: PayerRoutingStore }): React.ReactElement => (
        <PayerRoutingPanel payer={payer} bindings={store.getBindings(payer.id)} emrTags={payer.tags || []} />
    ),
);

const ReactiveDrawer = observer(
    ({
        payer,
        store,
        onClose,
    }: {
        payer: ProviderPayer;
        store: PayerRoutingStore;
        onClose: () => void;
    }): React.ReactElement => (
        <PayerDrawerPanel className="fixed">
            <PayerDrawer
                payer={payer}
                bindings={store.getBindings(payer.id)}
                emrTags={payer.tags || []}
                onClose={onClose}
            />
        </PayerDrawerPanel>
    ),
);

interface IPayersProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    toastContext: IToastContextProps;
}

/**
 * The filters a tab asks for, as this table's filter objects.
 *
 * The tab-to-filters rule is core's (`payerTabFilters`), so this console and Support cannot drift on what "halted"
 * means; only the shape of a filter object is local to the table (FE-93). There is no unconfigured queue here — that
 * tab is Support's view of payers this console created.
 */
const tabFilters = (tab: PayerTab): ICustomFilter[] =>
    payerTabFilters(tab, GENERATED_PAYER_NAME_PREFIX).map(filter => ({
        filterId: filter.filterId,
        filterMethod: FilterMethod[filter.method],
        filterType: 'String',
        value: filter.value,
        ...(filter.label ? { label: filter.label } : {}),
        ...(filter.locked ? { locked: true } : {}),
    }));


interface IPayersState {
    loading: boolean;
    /**
     * Which tab is showing: every payer, or the halted ones.
     *
     * No counts on these tabs yet. Core's `TabContainer` takes a `count` per tab and does not render it — the
     * component draws icon and label only — so asking the server for two totals would buy nothing. When it draws
     * them, `countByFilters` on the store is ready to supply both.
     */
    tab: PayerTab;
    /**
     * The payer whose drawer is open, by id.
     *
     * By ID rather than by object, so the drawer follows a refetch instead of rendering a
     * snapshot taken when it was opened — a payer's routing can change under it.
     */
    detailPayerId: string | undefined;
    /**
     * The payer just created with New payer, so its drawer can open even when the current page,
     * sort or filters do not include it — the drawer otherwise resolves only from the rows on
     * screen, and would silently not open.
     */
    createdPayer: ProviderPayer | undefined;
}

class PayersPage extends React.Component<IPayersProps, IPayersState> {
    private presenter: DataTablePresenter<ProviderPayer>;
    private columns: ITableColumn<ProviderPayer>[];
    /**
     * The options of the last fetch, to repeat it after a create. The presenter has no refetch of
     * its own; `DataTable` only calls `onFetch` when paging, sort or filters change.
     */
    private lastFetch: ITableFetchOptions | undefined;

    constructor(props: IPayersProps) {
        super(props);
        this.state = { loading: false, tab: 'enter', detailPayerId: undefined, createdPayer: undefined };
        // The same column set the support console renders. What differs is passed in, not
        // forked: where the join comes from, which tags are EMR payers, the header wording,
        // and no Halt column — halt shows as a badge in the name cell here.
        this.columns = [
            ...buildPayerColumns<ProviderPayer>({
                getBindings: id => this.store.getBindings(id),
                // Every ProviderTag IS an EMR payer — the type has exactly one member — so
                // unlike support there is nothing to filter out and no unknown case.
                getEmrTags: payer => payer.tags || [],
                identityHeader: 'Payer',
                identitySortType: ProviderPayerSortType.NAME,
            }),
            detailColumn(this.handleOpenDetail),
        ];
        this.presenter = new DataTablePresenter<ProviderPayer>({
            name: 'provider-payers',
            data: [],
            columns: this.columns,
            filterSchemas,
        });
    }

    /**
     * The tab the filters on screen were last written for.
     *
     * `state.tab` is for rendering and `setState` has not flushed while `handleTabChange` is still writing filters, so
     * the guard below would compare the new filters against the OLD tab and immediately undo the switch.
     */
    private appliedTab: PayerTab = 'enter';
    private disposeTabGuard: (() => void) | undefined;

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Payers';
        // A tab's filter can be edited out from under it: `locked` stops the chip being removed, not the Filters
        // panel from changing the row. When that happens the tab stops claiming to be itself rather than showing a
        // list that contradicts its own heading — see `payerTabHolds`.
        this.disposeTabGuard = mobx.reaction(
            () => this.presenter.filters.map(f => `${f.filterId} ${f.filterMethod} ${f.value}`).join(''),
            () => {
                if (this.appliedTab !== 'enter' && !payerTabHolds(this.appliedTab, GENERATED_PAYER_NAME_PREFIX, this.presenter.filters)) {
                    this.appliedTab = 'enter';
                    this.setState({ tab: 'enter' });
                }
            },
        );
    }

    /**
     * Switch tabs.
     *
     * Both tabs are the same list under a different filter, and the server applies it — the Halt tab is not a pass
     * over the rows that happen to be on screen, which would miss every halted payer past the first page (NS-3).
     * Only the tabs' own filters change hands — `filtersKeptOnTabSwitch`, the rule Support uses too — so a search, and
     * anything set in the Filters panel, stays where the user left it.
     *
     * A search still in flight is left to land, unlike on Support: here the box is not remounted by a tab switch and
     * keeps its text, and no tab owns `filterName`, so the pending search is still the one on screen.
     */
    private handleTabChange = (tabId: string): void => {
        const tab = tabId as PayerTab;
        if (tab === this.state.tab) {
            return;
        }

        const from = this.appliedTab;
        this.appliedTab = tab;
        this.setState({ tab, detailPayerId: undefined });
        this.presenter.updateFilters([
            ...filtersKeptOnTabSwitch(from, tab, GENERATED_PAYER_NAME_PREFIX, this.presenter.filters),
            ...tabFilters(tab),
        ]);
    };

    public componentWillUnmount(): void {
        // A keystroke inside the debounce window would otherwise fire after unmount and
        // mutate a presenter nobody is rendering.
        this.applySearch.cancel();
        if (this.disposeTabGuard) {
            this.disposeTabGuard();
        }
        this.props.authContext.cancelGroup('provider-payers');
    }

    public render(): React.ReactElement {
        return (
            <PageLayout>
                <PageHeader
                    title="Payers"
                    subtitle="Where each payer's claims, reconsiderations and paperwork are sent."
                    hideDateRange={true}
                    actions={
                        <BaseButton variant="primary" size="medium" onClick={this.handleNewPayer}>
                            <Icon path={mdiPlus} size={0.7} />
                            New payer
                        </BaseButton>
                    }
                />
                <PageLayoutRow expand={true}>
                    {/* Jordan Kelley, 2026-09-17: a tab in Payers showing the payers in HALT state, on this console
                        as well as Support's. The tab is a server-side filter, not a pass over the rows on screen. */}
                    {/* No children: `TabContainer` puts them in a `display: none` panel, which hid this entire
                        screen. The panel below is the page's own — see `S.TabPanel`. */}
                    <TabContainer
                        tabs={[
                            { id: 'enter', label: 'All payers' },
                            { id: 'halt', label: 'Halt' },
                        ]}
                        activeTab={this.state.tab}
                        onTabChange={this.handleTabChange}
                    />
                    <S.TabPanel role="tabpanel" id={`tabpanel-${this.state.tab}`}>
                        {this.state.tab === 'halt' && (
                            <S.NoticeSlot>
                                <Notice>{HALTED_PAYERS_NOTICE}</Notice>
                            </S.NoticeSlot>
                        )}
                        <DataTableCard size="large">
                            <S.TableScope>
                                <DataTable
                                    presenter={this.presenter}
                                    isLoading={this.state.loading}
                                    onFetch={this.onFetch}
                                    onRowClick={this.handleRowClick}
                                    renderRowSubComponent={this.renderDetail}
                                    renderControlsLeading={this.renderSearch}
                                    renderControlsExtra={this.renderControls}
                                    // Export is rendered by `renderControls` above, in the same row, so
                                    // the table's own export control would be a second one a few inches
                                    // away.
                                    showExport={false}
                                />
                            </S.TableScope>
                        </DataTableCard>
                    </S.TabPanel>
                </PageLayoutRow>
                {this.renderDetailDrawer()}
            </PageLayout>
        );
    }

    private get store(): PayerRoutingStore {
        return this.props.rootStore!.payerRoutingStore;
    }

    /**
     * Guards the payer-id lookup against itself.
     *
     * Each keystroke fires a request and they do not come back in order. Without this, a slow
     * lookup for "12" can land after a fast one for "12345" and repaint the list with the wrong
     * result — which reads as the search being broken rather than late.
     */
    private searchSeq = 0;

    private applySearch = _.debounce((value: string): void => {
        void this.runSearch(value);
    }, 300);

    /**
     * Search by payer name OR by clearinghouse payer id.
     *
     * The two cannot be one request. `filterName` and `filterId` are different filters and the
     * server ANDs different filters — it only ORs repeated values of ONE. So asking for "name
     * matches or id matches" in a single call returns the intersection, which is nothing.
     *
     * Instead the typed value is CLASSIFIED by asking the data, not by guessing at its shape: if
     * any clearinghouse connection carries it as an external id, it is a payer id and we fetch
     * those payers by id; otherwise it is a name and we fall back to `LK` on the name. A regex
     * for "looks like a payer id" would be wrong the first time a payer is called "3M".
     *
     * A failed lookup degrades to a name search rather than to an empty list: showing "no
     * payers" because a join failed is a wrong answer, not a slow one (NS-3).
     */
    private runSearch = async (raw: string): Promise<void> => {
        const value = raw.trim();
        const seq = ++this.searchSeq;

        // `locked` on every filter this writes. `ActiveFilters` renders each valid filter as a
        // chip with a dismiss cross, and dismissing the search's chip reset the results while
        // the box kept its text — two controls disagreeing about what is filtering. Locked
        // chips still SAY what is being searched; they just cannot be removed behind the box's
        // back. The box's own clear is the way to clear it.
        const apply = (next: ICustomFilter[]): void => {
            // Replace BOTH search filters in one update. Two calls would leave a stale
            // `filterName` alongside a new `filterId`, and the server ANDs them — an empty list
            // for a search that matched. Filters set in the panel are preserved.
            const others = this.presenter.filters.filter(
                filter => filter.filterId !== 'filterName' && filter.filterId !== 'filterId',
            );
            this.presenter.updateFilters([...others, ...next]);
        };

        if (!value) {
            apply([]);
            return;
        }

        let payerIds: string[] = [];
        try {
            payerIds = await this.store.getPayerIdsByExternalId(value);
        } catch {
            // Fall through to a name search — never to a confident empty list.
            payerIds = [];
        }

        if (seq !== this.searchSeq) {
            return;
        }

        if (payerIds.length) {
            apply(
                payerIds.map(id => ({
                    filterId: 'filterId',
                    filterMethod: FilterMethod.EQ,
                    filterType: 'String',
                    value: id,
                    locked: true,
                })),
            );
            return;
        }

        apply([
            { filterId: 'filterName', filterMethod: FilterMethod.LK, filterType: 'String', value, locked: true },
        ]);
    };

    /**
     * ONE control row, not two.
     *
     * The search box goes in the table's own controls row beside the Filter button rather than
     * in a second row beneath it — the FE-23 complaint, which this screen had reproduced.
     * `renderControlsExtra` alone could not do it: it renders after pagination and the column
     * picker, which is the wrong end of the row for a search box, so `DataTable` grew a leading
     * slot. Both slots are optional and inert, so the ~124 other screens rendering that
     * component are untouched.
     */
    private renderSearch = (): React.ReactElement => <PayersSearch onSearch={this.applySearch} />;

    /**
     * Export and expand/collapse, at the end of the same row.
     *
     * The shared table exposes no `toggleAllRowsExpanded`, but each row carries
     * `toggleRowExpanded` from `useExpanded` — so this works without changing the table.
     */
    private renderControls = (
        rows: Array<Row<ProviderPayer>>,
        prepareRow: (row: Row<ProviderPayer>) => void,
    ): React.ReactElement => {
        const anyExpanded = rows.some(row => row.isExpanded);

        return (
            <S.ToolbarRight>
                <TableExport rows={rows} prepareRow={prepareRow} filename="payers" />
                {rows.length > 0 && (
                    <BaseButton
                        variant="outlined"
                        size="small"
                        onClick={(): void => rows.forEach(row => row.toggleRowExpanded(!anyExpanded))}
                    >
                        <Icon path={anyExpanded ? mdiUnfoldLessHorizontal : mdiUnfoldMoreHorizontal} size={0.7} />
                        {anyExpanded ? 'Collapse all' : 'Expand all'}
                    </BaseButton>
                )}
            </S.ToolbarRight>
        );
    };

    private handleRowClick = (row: Row<ProviderPayer>): void => {
        row.toggleRowExpanded();
    };

    private handleOpenDetail = (payer: ProviderPayer): void => {
        this.setState({ detailPayerId: payer.id });
    };

    private handleNewPayer = (): void => {
        const { dialogContext } = this.props;

        dialogContext.showDialog({
            props: {
                renderTitle: 'New payer',
                onClose: dialogContext.hideDialog,
                // A half-typed payer should not vanish on a stray click outside the dialog.
                disableBackdropClick: true,
            },
            Component: (
                <PayerCreateForm
                    notice={newPayerNotice}
                    onCancel={dialogContext.hideDialog}
                    onSubmit={this.createPayer}
                />
            ),
        });
    };

    /**
     * Create, then show the user what they made.
     *
     * The toast carries the name the SERVER returned, not the one typed, so the "Generated Payer"
     * prefix is visible as a fact rather than a promise. The drawer opens on the new payer, where
     * it shows no clearinghouse connection — true until ENTER Support configures it (NS-3).
     *
     * On failure the dialog stays open with what was typed, under the standard error dialog.
     */
    private createPayer = async (payload: IPayerCreatePayload): Promise<void> => {
        const { dialogContext, toastContext } = this.props;

        let created: ProviderPayer;
        try {
            created = await this.store.createPayer(payload);
        } catch (err) {
            dialogContext.showError(err as Error);
            return;
        }

        dialogContext.hideDialog();
        toastContext.notify?.(`Payer created: ${created.name}`, 'right');
        this.setState({ createdPayer: created, detailPayerId: created.id });

        // The drawer reads connections from the store, and `onFetch` only joins the rows on the
        // current page — so ask for this payer's explicitly, or a create that lands on another
        // page opens a drawer stuck on "Loading".
        void this.store.loadForPayers([created.id]);
        if (this.lastFetch) {
            await this.onFetch(this.lastFetch);
        }
    };

    /**
     * The drawer, rendered from `render` rather than from a table callback.
     *
     * `renderToolbar` and the cell renderers run inside `DataTable`'s own observer, and reading
     * component state from there throws — mobx-react refuses non-observable state inside someone
     * else's derivation. The payer is resolved from the presenter every render so the drawer
     * follows refetches.
     */
    private renderDetailDrawer = (): React.ReactElement | null => {
        const { detailPayerId, createdPayer } = this.state;
        if (!detailPayerId) {
            return null;
        }

        const payer =
            this.presenter.data.find(p => p.id === detailPayerId) ??
            (createdPayer?.id === detailPayerId ? createdPayer : undefined);
        if (!payer) {
            return null;
        }

        return (
            <ReactiveDrawer
                payer={payer}
                store={this.store}
                onClose={(): void => this.setState({ detailPayerId: undefined })}
            />
        );
    };

    private renderDetail = (row: Row<ProviderPayer>): React.ReactElement => (
        <ReactiveRoutingPanel payer={row.original} store={this.store} />
    );

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        this.lastFetch = fetchOptions;
        try {
            this.setState({ loading: true });
            this.props.authContext.cancelGroup('provider-payers');
            this.props.authContext.setGroup('provider-payers');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, this.columns);
            const listing = await this.store.loadListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: validFilters,
            });

            this.presenter.onDataUpdate(listing.items);
            this.presenter.onDataTotalChange(listing.total);

            // Join clearinghouse connections for the rows now on screen. Fire-and-forget: the
            // cells observe the store and fill in when it resolves, so a slow or failed join
            // degrades to "unknown" rather than holding up the table.
            void this.store.loadForPayers(listing.items.map(payer => payer.id));

            this.setState({ loading: false });
        } catch (err) {
            if ((err as Error | undefined)?.name === 'AbortError') {
                // Cancelled by a newer fetch (`cancelGroup` above) or by leaving the page. The newer fetch owns the
                // table now — its answer or its failure settles the rows and the spinner — so this one touches neither.
                return;
            }
            // The newest request failed, so the rows on screen are still the PREVIOUS request's answer — after a tab
            // switch, the last tab's payers under this tab's heading, which on the Halt tab is an assertion about
            // routing we have not made (NS-3; measured on Support's twin of this page). An empty list claims nothing;
            // the error dialog says why it is empty.
            this.presenter.onDataUpdate([]);
            this.presenter.onDataTotalChange(0);
            this.setState({ loading: false });
            this.props.dialogContext.showError(err);
        }
    };
}

export const Payers: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        if (!rootStore) {
            return <></>;
        }

        return (
            <PayersPage
                authContext={authContext}
                dialogContext={dialogContext}
                toastContext={toastContext}
                rootStore={rootStore}
            />
        );
    }),
);
